import * as React from 'react';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { useDebounce } from '../../hooks/useDebounce';
import { cn } from '../../lib/utils';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '../Select';
import { TrashIcon, XIcon } from 'lucide-react';
import Button from '../../components/Button';
import Input from '../../components/Input';
import { Button as ShadcnButton } from '../Button';
import dayjs from 'dayjs';
import { DataTableFilterOption, FilterType, Operator } from './types';
import { AdvancedFacetedFilter } from './AdvancedFacetedFilter';
import { SingleDateSelector } from '../../components/DateSelector';
import { NexoyaContentFilterFieldName, NexoyaContentFilterOperator } from '../../types';
import { DAYS_AGO_UNIT, getFilterChipParts, getIconForField, joinFilterChipValueText } from './utils';
import Tooltip from '../../components/Tooltip';
import { AdvancedTitleFilter } from './AdvancedTitleFilter';

/**
 * The chip's ground, lifted off the trigger so the inline ✕ sits INSIDE the pill. It
 * mirrors `SecondaryContained` — white, a `paleLilac66` hairline plus its drop shadow,
 * full `paleLilac` on hover — so a removable chip reads like every other filter chip.
 */
const CHIP_PILL_CLASSES = cn(
  'inline-flex max-w-full items-center rounded-[4px] bg-white',
  'shadow-[0_0_0_1px_rgba(223,225,237,0.66),0_2px_4px_-1px_rgba(7,97,52,0.24)]',
  'transition-shadow hover:shadow-[0_0_0_1px_#dfe1ed]',
  // `has-[:focus-visible]`, not `focus-within`, which lights on every mouse click.
  'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-green-400 has-[:focus-visible]:ring-offset-1',
);

interface DataTableFilterItemProps {
  selectedOption: DataTableFilterOption;
  selectedOptions: DataTableFilterOption[];
  setSelectedOptions: Dispatch<SetStateAction<DataTableFilterOption[]>>;
  defaultOpen: boolean;
  setShouldFetch: Dispatch<SetStateAction<boolean>>;
  buttonClassName?: string;
  /**
   * Drive the popover from the outside. Omit it and the chip keeps managing its own open
   * state from `defaultOpen`, which is what every existing call site relies on. A parent
   * that shows several chips and must guarantee only one is open at a time needs this:
   * Radix re-closes a popover opened by the same click that closed another one.
   */
  open?: boolean;
  /** Fires with the state the chip WANTS. Required to be useful alongside `open`. */
  onOpenChange?: (open: boolean) => void;
  /**
   * Edit this clause as a LIST of OR-ed keywords rather than as one value, whatever its
   * field. Title and ParentTitle already do this by name; a surface whose vocabulary is
   * scraped from the rows in scope (labels' Objective / Bid Strategy / Channels) needs the
   * same editor, because the faceted list can only render values present in `options` and
   * would make a saved out-of-vocabulary value invisible AND unremovable.
   */
  keywordEditor?: boolean;
  /** Offered as one-click "+ value" adds inside the keyword editor. */
  suggestions?: string[];
  /** How many values the chip prints before collapsing to "N selected". Three by default. */
  collapseThreshold?: number;
  /**
   * Hang an inline ✕ off the chip's right edge, inside the pill. Opt-in: without it the
   * only remove control is the trash inside the popover, which is what every existing
   * call site reaches for. The trash stays either way.
   *
   * The chip does NOT remove itself here — it reports the intent and the caller owns the
   * clause list, exactly as it owns `open`.
   */
  onRemove?: () => void;
  renderEditor?: React.ReactNode;
  popoverClassName?: string;
}

export function FilterItem({
  selectedOption,
  selectedOptions,
  setSelectedOptions,
  defaultOpen,
  setShouldFetch,
  buttonClassName,
  open: openProp,
  onOpenChange,
  keywordEditor = false,
  suggestions,
  collapseThreshold,
  onRemove,
  renderEditor,
  popoverClassName,
}: DataTableFilterItemProps) {
  const selectedValues = useMemo(() => new Set(selectedOption.filterValues), [selectedOption.filterValues]);

  const filterValues = Array.from(selectedValues);
  const [value, setValue] = useState<any>(filterValues.length > 1 ? filterValues : (filterValues[0] ?? ''));
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isOpenControlled = openProp !== undefined;
  const open = isOpenControlled ? openProp : uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (!isOpenControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  const [hasDuplicate, setHasDuplicate] = useState(false);

  const debounceValue = useDebounce(value as string, 500);
  const operators: Operator[] = selectedOption.operators.sort((a, b) => a.label.localeCompare(b.label));

  const [filterState, setFilterState] = useState<Record<string, string | null>>({});
  const [selectedOperator, setSelectedOperator] = useState<Operator>(
    operators?.find((c) => c.value === selectedOption.filterOperator) ?? operators?.[0],
  );

  const usesKeywordEditor =
    keywordEditor ||
    selectedOption.value === NexoyaContentFilterFieldName.Title ||
    selectedOption.value === NexoyaContentFilterFieldName.ParentTitle;

  useEffect(() => {
    const hasDuplicate = selectedOptions.some(
      (item) =>
        item.type === selectedOption.type &&
        item.id !== selectedOption.id &&
        item.value === selectedOption.value &&
        item.filterOperator === selectedOperator.value &&
        selectedOption.type === 'date',
    );

    setHasDuplicate(hasDuplicate);
  }, [selectedOptions, selectedOption, selectedOperator]);

  useEffect(() => {
    const newStateValue =
      selectedOption.options.length > 0
        ? filterValues.length > 0
          ? `${filterValues.join('.')}~${selectedOperator?.value}`
          : null
        : debounceValue.length > 0
          ? `${debounceValue}~${selectedOperator?.value}`
          : null;

    if (filterState[selectedOption.id] !== newStateValue) {
      setFilterState((prevState) => ({
        ...prevState,
        [selectedOption.id]: newStateValue,
      }));
    }
  }, [selectedOption, debounceValue, selectedOperator, filterValues, filterState]);

  // Whether the child editor — not this component's local `value` — owns the clause's values.
  // `value` is seeded ONCE on mount and is only ever written by the free-text input and the
  // date picker, so for these types it goes stale the moment the child commits a keyword or
  // ticks a box. Rewriting `filterValues` from it below then wipes everything the child wrote.
  const isValueOwnedByChild =
    usesKeywordEditor ||
    selectedOption.type === 'stringArr' ||
    selectedOption.type === 'numberArr' ||
    selectedOption.type === 'boolean';

  useEffect(() => {
    setSelectedOptions((prev) => {
      return prev.map((item) =>
        item.id === selectedOption.id
          ? isValueOwnedByChild
            ? {
                ...item,
                // Read the values back off the clause rather than off the stale local `value`:
                // that preserves whatever the child committed, while still NORMALISING a
                // freshly picked clause. Catalogue entries carry no `filterValues` at all and
                // `getFilterValueInputBasedOnType` is called without a `|| []` guard, so a
                // clause left undefined here reaches the mutation as an omitted value.
                filterValues: item.filterValues ?? [],
                filterOperator: selectedOperator?.value,
              }
            : {
                ...item,
                filterValues: Array.isArray(value) ? value : value ? [value] : [],
                filterOperator: selectedOperator?.value,
              }
          : item,
      );
    });
  }, [value, selectedOperator, selectedOption.id, isValueOwnedByChild]);

  const isDaysAgoField = selectedOption.value === NexoyaContentFilterFieldName.EarliestMeasurementDataDaysAgo;
  // A whole day count; 0–30 is the usual range, but longer look-backs stay allowed.
  const daysAgoInputProps = isDaysAgoField
    ? {
        type: 'number',
        min: 0,
        step: 1,
        placeholder: '0–30',
        endAdornment: <span className="ml-2 shrink-0 text-xs text-muted-foreground">{DAYS_AGO_UNIT}</span>,
      }
    : {};

  const renderFilterBasedOnType = (type: FilterType, withKeywordEditor: boolean) => {
    if (renderEditor) return renderEditor;

    if (withKeywordEditor) {
      return (
        <AdvancedTitleFilter
          key={selectedOption.id}
          setShouldFetch={setShouldFetch}
          selectedValues={selectedValues}
          setSelectedOptions={setSelectedOptions}
          selectedOption={selectedOption}
          selectedOperator={selectedOperator}
          suggestions={suggestions}
        />
      );
    }

    switch (type) {
      case 'string':
      case 'number':
        return (
          <Input
            autoFocus
            placeholder="Type here..."
            className="!focus:shadow-none !mt-2 h-8 w-full"
            color="dark"
            value={value}
            onChange={(event) => {
              setValue(event.target.value);
              setShouldFetch(false);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                setOpen(false);
                setShouldFetch(true);
              }
            }}
            {...daysAgoInputProps}
          />
        );
      case 'stringArr':
      case 'numberArr':
      case 'boolean':
        return (
          <AdvancedFacetedFilter
            fieldType={type}
            key={selectedOption.id}
            title={selectedOption.label}
            options={selectedOption.options}
            selectedValues={selectedValues}
            setSelectedOptions={setSelectedOptions}
            selectedOption={selectedOption}
            selectedOperator={selectedOperator}
          />
        );
      case 'date':
        return (
          <div className="flex w-full items-center space-x-1">
            <SingleDateSelector
              panelProps={{ popperProps: { style: { zIndex: 3600, pointerEvents: 'auto' } } }}
              style={{ width: '100% !important' }}
              disableAfterDate={null}
              showAsRange={false}
              selectedDate={dayjs(value).toDate() || null}
              renderInputValue={(date) =>
                dayjs(date).isValid() ? dayjs(date).format('DD MMM, YYYY') : 'Select a date'
              }
              onDateChange={({ selectedDate }) => {
                setValue(dayjs(selectedDate).format('DD MMM, YYYY'));
                setSelectedOptions((prev) => {
                  return prev.map((item) =>
                    item.id === selectedOption.id
                      ? {
                          ...item,
                          filterValues: [dayjs(selectedDate).format('DD MMM, YYYY')],
                          filterOperator: selectedOperator?.value,
                        }
                      : item,
                  );
                });
              }}
              className="!mt-2 !h-8 !w-full !border-neutral-500 !bg-popover !text-neutral-300 !shadow-accent-dark"
            />
          </div>
        );
      default:
        return null;
    }
  };

  // ONE computation behind both the pixels and the spoken name. `hasChipValue` is read off
  // the PARTS rather than off the joined string, because a clause holding a single blank
  // keyword joins to '' yet has always counted as valued — see `chipParts.test.ts`, which
  // pins this derivation against the shipped `getFilterChipDisplayText` case for case.
  const chipParts = getFilterChipParts({
    selectedOption,
    selectedValues,
    freeTextValue: typeof value === 'string' ? value : '',
    selectedOperator,
    collapseThreshold,
  });
  const hasChipValue = chipParts.collapsed !== null || chipParts.values.length > 0;
  const chipValueText = hasChipValue ? joinFilterChipValueText(chipParts) : '';

  const chipTrigger = (
    <PopoverTrigger asChild>
      <Button
        variant="contained"
        color="secondary"
        size="small"
        // Named from the same parts it prints. Left to the DOM, the name is assembled from
        // the spans below and the space that follows the colon — which lives at the end of
        // its own span, where the name computation trims it — so the chip READ
        // "Title: Contains nike" but was ANNOUNCED "Title:Contains nike".
        aria-label={hasChipValue ? `${selectedOption.label}: ${chipValueText}` : selectedOption.label}
        onClick={() => setOpen(true)}
        startAdornment={getIconForField(selectedOption.value as NexoyaContentFilterFieldName)}
        className={cn(
          'gap-1.5 !truncate !px-3 !py-2',
          hasDuplicate && '!bg-red-100',
          // The wrapper below owns the ground and hairline in this mode, and the ✕ takes
          // the right half of the padding.
          onRemove && '!bg-transparent !pr-1.5 !shadow-none',
          buttonClassName,
        )}
      >
        <Tooltip
          open={hasDuplicate}
          popperProps={{
            style: { zIndex: 38000 },
            modifiers: {
              offset: {
                offset: '0, 14',
              },
            },
          }}
          variant="dark"
          size="small"
          placement="top"
          content={hasDuplicate ? 'Adjust the operator to avoid duplicate filters.' : ''}
        >
          <div>
            <span className="font-light capitalize">{selectedOption.label}</span>
            {hasChipValue && (
              <>
                <span className="font-light">: </span>
                <span className="font-light text-muted-foreground">{chipValueText}</span>
              </>
            )}
          </div>
        </Tooltip>
      </Button>
    </PopoverTrigger>
  );

  return (
    <Popover
      open={open}
      key={selectedOption.id}
      onOpenChange={(changedValue) => {
        setOpen(changedValue);
        if (!changedValue) {
          setShouldFetch(true);
        }
      }}
    >
      {onRemove ? (
        // The ✕ is a SIBLING of the trigger, never a child: the trigger already renders a
        // real <button> (`ButtonBase` mounts `component='button'`), so nesting would be
        // invalid HTML and would stack two overlapping role="button" nodes on the same
        // pixels. The wrapper is what makes them read as one pill.
        <span className={cn(CHIP_PILL_CLASSES, hasDuplicate && 'bg-red-100')}>
          {chipTrigger}
          <button
            type="button"
            aria-label={`Remove ${selectedOption.label} filter`}
            // Without this the click also reaches the trigger's ground and opens the
            // popover of a clause that is on its way out.
            onClick={(event) => {
              event.stopPropagation();
              onRemove();
            }}
            className="mr-2 inline-flex size-4 shrink-0 items-center justify-center rounded-[2px] text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700 focus-visible:outline-none"
          >
            <XIcon className="size-3" aria-hidden="true" />
          </button>
        </span>
      ) : (
        chipTrigger
      )}
      <PopoverContent className={cn('w-80 space-y-1.5 p-2', popoverClassName)} align="start">
        <div className="flex items-center space-x-1 pl-1 pr-0.5">
          <div className="flex flex-1 items-center space-x-1">
            <div className="max-w-36 truncate text-ellipsis text-xs capitalize text-muted-foreground">
              {selectedOption.label}
            </div>
            <Select
              value={selectedOperator?.value}
              onValueChange={(value) => {
                setSelectedOperator(operators.find((c) => c.value === value));
                setSelectedOptions((prev) => {
                  return prev.map((item) =>
                    item.id === selectedOption.id
                      ? {
                          ...item,
                          filterOperator: value as NexoyaContentFilterOperator,
                        }
                      : item,
                  );
                });
              }}
            >
              <SelectTrigger className="h-auto w-fit truncate border-none px-2 py-0.5 text-xs hover:bg-muted/50">
                <SelectValue placeholder={selectedOperator?.label} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {operators?.map((item) => (
                    <SelectItem key={item.value} value={item.value} className="py-1">
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <ShadcnButton
            aria-label="Remove filter"
            variant="ghost"
            size="icon"
            className="size-7 text-muted-foreground hover:bg-neutral-600 hover:text-neutral-300"
            onClick={() => {
              setSelectedOptions((prev) => prev.filter((item) => item.id !== selectedOption.id));
              setFilterState((prevState) => ({
                ...prevState,
                [selectedOption.id]: null,
              }));
              setShouldFetch(true);
            }}
          >
            <TrashIcon className="size-3" aria-hidden="true" />
          </ShadcnButton>
        </div>
        {renderFilterBasedOnType(selectedOption.type, usesKeywordEditor)}
      </PopoverContent>
    </Popover>
  );
}
